import { ChangeDetectorRef, Directive, ElementRef, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';

import { NotebookVizResult } from '@mode/notebooks/domain';
import { LegacyAssetService } from '@mode/shared/contract-common';
import { setIFrameSrc } from '@mode/shared/util-js';
import { BugsnagService } from '@mode/shared/util-error-handling';
import { Broadcaster, Emit, EmitMessages, Rpc, RpcMessages } from '@mode/shared/util-iframe';

// HTMLWidgets from R would normally call staticRender on DOMContentLoaded,
// but that is impossible when we are dynamically loading the scripts,
// so this instead calls staticRender manually. Debounce is in there incase
// there are many R cell cells in a single report.
const scriptToFakeDOMContentLoaded = `<script>
    if (window.HTMLWidgets && window.HTMLWidgets.staticRender) {
      clearTimeout(window.staticRenderDebounce);
      window.staticRenderDebounce = setTimeout(function() {
        window.HTMLWidgets.staticRender();
      }, 500);
    }
  </script>`;

@Directive({
  selector: '[modeNotebookCellResultIframeContent]',
})
export class NotebookCellResultIframeContentDirective implements OnInit, OnDestroy, OnChanges {
  @Input()
  modeNotebookCellResultIframeContent!: NotebookVizResult;

  private _destroy$ = new Subject<void>();
  private iframeUrl = this.assetService.pythonIframeNg;
  private iframeContainer: HTMLElement = this.el.nativeElement;
  private iframe = document.createElement('iframe');
  constructor(
    private el: ElementRef<HTMLInputElement>,
    private assetService: LegacyAssetService,
    private _bugsnagService: BugsnagService,
    private window: Window,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.iframeContainer = this.el.nativeElement;
  }
  ngOnChanges() {
    const cell = this.modeNotebookCellResultIframeContent;
    const url = `${this.iframeUrl}?id=${cell.token}`;

    this.iframe.id = 'mode-notebook-cell-result-' + cell.token;
    setIFrameSrc(this.iframe, url);
    this._bugsnagService.leaveBreadcrumb('Load notebook cell cell iframe', { url });

    // If the cell is a table, make sure dates don't get wrapped
    const html = cell.content
      .toString()
      .replace(/<td>(\d\d\d\d-\d\d-\d\d)\b/g, '<td><span style="white-space: nowrap">$1</span>');

    Emit.getMessageStream(this.window)
      .pipe(
        filter((f) => f.type === EmitMessages.MessageTypes.Ready && f.id === this.iframe.src),
        takeUntil(this._destroy$), // Can receive multiple messages if the iframe reloads due to things like move cell up
        switchMap(() =>
          Rpc.sendRequest(
            this.window,
            this.iframe.contentWindow as Broadcaster,
            new RpcMessages.InitCellResults(html + scriptToFakeDOMContentLoaded)
          )
        )
      )
      .subscribe(
        () => {},
        (err: any) => {
          this._bugsnagService.notify(err, (event: any) => {
            event.groupingHash = 'CellResultRpcTimeout';

            event.addMetadata('debugInfo', {
              cellToken: cell.token,
              iframeUrl: url,
              iframeMounted: document.body.contains(this.iframe),
            });
          });
        }
      );

    this.iframeContainer.appendChild(this.iframe);
    this.cdr.detectChanges();
  }
  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
